/**
 * Starts a snapshot, dispatches the GitHub Actions workflow and polls the run.
 *
 * Uses existing endpoints (the transport pipeline logic is frozen):
 *   POST /api/cicd/transport/snapshot   (partnerIds -> PD files land in the snapshot)
 *   POST /api/cicd/transport/dispatch   -> {run:{id,url}} or {status:"pending_approval"}
 *   GET  /api/cicd/pipeline/overview?spaceId=...  (polled every ~3s)
 *
 * Partner Directory entries only ever travel through the pipeline: partnerIds
 * go into the snapshot and are applied by the sync-partner-directory.yml
 * workflow on the target. There is no direct write path from this hook.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../utils/helpers";

export function useCicdRun() {
  const [state, setState] = useState("idle"); // idle|snapshotting|dispatching|running|pending|done|failed
  const [runId, setRunId] = useState(null);
  const [runUrl, setRunUrl] = useState(null);
  const [runInfo, setRunInfo] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startPolling = useCallback((spaceId, id) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const tick = async () => {
      try {
        const r = await api(`/api/cicd/pipeline/overview?spaceId=${encodeURIComponent(spaceId)}`);
        const runs = Array.isArray(r?.runs) ? r.runs : [];
        const run = runs.find((x) => String(x.id) === String(id));
        if (run) {
          setRunInfo(run);
          if (run.status === "completed") {
            if (timerRef.current) clearInterval(timerRef.current);
            if (run.conclusion === "success") setState("done");
            else setState("failed");
          }
        }
      } catch {
        /* Ignore errors here; the next tick retries. */
      }
    };
    tick();
    timerRef.current = setInterval(tick, 3000);
  }, []);

  const start = useCallback(async ({ spaceId, sourceInstanceId, packageId, packageName, artifactIds, werke, configsByWerk, partnerIds, snapshotPartnerIds, pdStagesByPid, commitMessage, targetStage }) => {
    // The snapshot carries ALL selected partner ids with their stage filter
    // (.stages.json per id); the dispatch only sends the ids for the current
    // target stage. If snapshotPartnerIds is omitted, the snapshot falls back
    // to partnerIds, so callers using the old signature keep working.
    const pidsFuerSnapshot = Array.isArray(snapshotPartnerIds) ? snapshotPartnerIds : (Array.isArray(partnerIds) ? partnerIds : []);
    const pidsFuerDispatch = Array.isArray(partnerIds) ? partnerIds : [];
    setState("snapshotting");
    setError(null);
    setRunId(null);
    setRunUrl(null);
    setRunInfo(null);
    let snapshotRes;
    try {
      snapshotRes = await api("/api/cicd/transport/snapshot", {
        method: "POST",
        timeout: 180000,
        body: JSON.stringify({
          spaceId,
          sourceInstanceId,
          packageId,
          packageName,
          artifactIds,
          // Selected plants: the snapshot materialises one package folder per suffix with
          // suffixed artifact ids. Empty means no split.
          werke: Array.isArray(werke) ? werke : [],
          // Parameter values per plant and stage. The backend turns them into SAP's
          // Configuration/ConfigParams_INTEGRATION_FLOW.json per target package and merges them
          // with the Git state, so values of other stages survive.
          configsByWerk: configsByWerk && typeof configsByWerk === "object" ? configsByWerk : {},
          // Selected partner ids to include in the snapshot: the backend
          // exports them under IntegrationPackages/<pkg>/PartnerDirectory/<Pid>/... .
          // Without partnerIds the snapshot is unchanged; PD files are only
          // written when a non-empty list arrives here.
          partnerIds: pidsFuerSnapshot,
          // Per-id set of target stages. If an id has no entry, the backend
          // writes "*" into .stages.json, matching the old behavior of
          // applying to all stages.
          pdStagesByPid: pdStagesByPid && typeof pdStagesByPid === "object" ? pdStagesByPid : {},
          commitMessage: commitMessage || `Transport ${packageName || packageId}`,
        }),
      });
    } catch (e) {
      setError(e?.message || "Snapshot failed");
      setState("failed");
      throw e;
    }

    // The target packages come from the snapshot response, they are NOT rebuilt here. The
    // snapshot just created them and knows their ids; a second naming rule maintained in
    // parallel in the frontend is exactly the kind of drift that causes wrong target ids.
    const zielPakete = (Array.isArray(snapshotRes?.pakete) ? snapshotRes.pakete : [])
      .map((p) => ({ packageId: p.packageId, packageName: p.packageName, artifactIds: p.artefakte || [] }))
      .filter((p) => p.packageId);
    if (!zielPakete.length) {
      const msg = "The snapshot reported no target package - the transport will not be started.";
      setError(msg);
      setState("failed");
      throw new Error(msg);
    }

    setState("dispatching");
    let dispatchRes;
    try {
      dispatchRes = await api("/api/cicd/transport/dispatch", {
        method: "POST",
        // One run per target package, started in sequence. With several plants this takes
        // correspondingly longer than the single call it used to be.
        timeout: 120000,
        body: JSON.stringify({
          spaceId,
          // No silent fallback to "qas" anymore: the backend validates the
          // target stage and reports a clear error if it is missing or not
          // further along the path. Guessing here is riskier than failing
          // loudly.
          targetStage: String(targetStage || "").toLowerCase(),
          pakete: zielPakete,
          // Selected Partner Directory ids: trigger the PD merge/upsert in
          // the target workflow. Only the ids released for the current
          // target stage go here; ids meant for a later stage (e.g. PROD)
          // stay in the snapshot but are not part of this dispatch.
          partnerIds: pidsFuerDispatch.join(","),
          commitMessage: commitMessage || `Transport ${packageName || packageId}`,
        }),
      });
    } catch (e) {
      setError(e?.message || "Workflow start failed");
      setState("failed");
      throw e;
    }

    if (dispatchRes?.status === "pending_approval") {
      setState("pending");
      return { pendingApproval: true };
    }

    const laeufe = Array.isArray(dispatchRes?.laeufe) ? dispatchRes.laeufe : [];
    // Do not swallow partial failures: whatever runs, runs - but the user has to see which
    // package did not start, otherwise the transport looks complete.
    if (dispatchRes?.teilfehler) setError(dispatchRes.teilfehler);

    const id = dispatchRes?.run?.id;
    const url = dispatchRes?.run?.url;
    if (id) {
      setRunId(id);
      setRunUrl(url || null);
      setState("running");
      startPolling(spaceId, id);
      return { pendingApproval: false, runId: id, runUrl: url, laeufe, pakete: zielPakete };
    }
    // No run id: mark as "running" but skip detail polling.
    setState("running");
    return { pendingApproval: false, laeufe, pakete: zielPakete };
  }, [startPolling]);

  const stop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  return { state, runId, runUrl, runInfo, error, start, stop };
}
